if (!customElements.get('vanta-quantity-offer')) {
  customElements.define('vanta-quantity-offer', class extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      this.variants = this.readJson('[data-vanta-variants]');
      this.optionNames = this.readJson('[data-vanta-option-names]');
      this.initialVariant = this.variantFromUrl();

      try {
        const response = await fetch(`/apps/vanta/commerce/offers?product_id=${encodeURIComponent(this.dataset.productId)}`);
        const data = await response.json();
        if (!response.ok || !data.offer) return;

        this.offer = data.offer;
        this.selectedTierId = this.defaultTier().id;
        this.selections = [];
        this.hidden = false;
        this.closest('.product__info-container')?.classList.add('vanta-offer-active');
        this.render();
        this.syncProductGalleryForVariant(this.initialVariant);
        this.onViewportChange = () => this.updateMobileBuyBar();
        window.addEventListener('scroll', this.onViewportChange, { passive: true });
        window.addEventListener('resize', this.onViewportChange);
      } catch (error) {
        console.error('VANTA offer could not be loaded', error);
      }
    }

    readJson(selector) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '[]');
      } catch {
        return [];
      }
    }

    variantFromUrl() {
      const variantId = new URLSearchParams(window.location.search).get('variant');
      return this.variants.find((variant) => String(variant.id) === variantId) || null;
    }

    defaultTier() {
      return this.offer.tiers.find((tier) => tier.isDefault) || this.offer.tiers[0];
    }

    selectedTier() {
      return this.offer.tiers.find((tier) => tier.id === this.selectedTierId) || this.defaultTier();
    }

    optionCount() {
      return this.variants[0]?.options?.length || this.optionNames.length || 1;
    }

    resizeSelections(quantity) {
      const optionCount = this.optionCount();
      this.selections = Array.from({ length: quantity }, (_, index) => {
        const current = this.selections[index] || [];
        return Array.from({ length: optionCount }, (_, optionIndex) => current[optionIndex] || '');
      });
    }

    optionValues(optionIndex) {
      return [...new Set(this.variants.map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
    }

    isColorOption(optionIndex) {
      return /colou?r|tono/.test(String(this.optionNames[optionIndex] || '').toLowerCase());
    }

    isSizeOption(optionIndex) {
      return /talla|size|tamano|tamaño/.test(String(this.optionNames[optionIndex] || '').toLowerCase());
    }

    optionName(optionIndex) {
      if (this.isColorOption(optionIndex)) return 'Color';
      if (this.isSizeOption(optionIndex)) return 'Talla';
      return this.optionNames[optionIndex] || `Opción ${optionIndex + 1}`;
    }

    findMatchingVariant(options, availableOnly = true) {
      return this.variants.find((variant) => {
        if (availableOnly && !variant.available) return false;
        return variant.options?.every((value, index) => !options[index] || options[index] === value);
      });
    }

    selectedVariant(pairIndex) {
      const options = this.selections[pairIndex] || [];
      if (options.some((option) => !option)) return null;
      return this.findMatchingVariant(options);
    }

    isOptionAvailable(pairIndex, optionIndex, value) {
      const options = [...(this.selections[pairIndex] || [])];
      options[optionIndex] = value;
      return Boolean(this.findMatchingVariant(options));
    }

    basePrice() {
      return Number(this.variants.find((variant) => variant.available)?.price || this.variants[0]?.price || 0);
    }

    baseVariant() {
      return this.variants.find((variant) => variant.available) || this.variants[0] || null;
    }

    tierDiscount(tier, subtotal, compareAtTotal) {
      const value = Math.max(0, Number(tier.discountValue) || 0);
      if (tier.discountType === 'fixed_amount') return value * 100;
      if (tier.discountType === 'fixed_price') return Math.max(0, subtotal - value * 100);
      if (tier.discountType === 'percentage') {
        const comparisonTotal = compareAtTotal || subtotal;
        const baseDiscount = Math.max(0, comparisonTotal - subtotal);
        return Math.max(0, comparisonTotal * Math.min(100, value) / 100 - baseDiscount);
      }
      return 0;
    }

    roundUpToNextNineHundred(amount) {
      if (!Number.isFinite(amount) || amount <= 0) return 0;
      return Math.ceil((amount - 90000) / 100000) * 100000 + 90000;
    }

    tierPricing(tier) {
      const variants = Array.from({ length: tier.quantity }, (_, index) => this.selectedVariant(index) || this.baseVariant());
      const subtotal = variants.map((variant) => Number(variant?.price || this.basePrice()))
        .reduce((total, price) => total + price, 0);
      const hasCompareAtPrices = variants.every((variant) => Number(variant?.compareAtPrice) > Number(variant?.price));
      const compareAtTotal = hasCompareAtPrices
        ? variants.reduce((total, variant) => total + Number(variant?.compareAtPrice || 0), 0)
        : null;
      const requestedDiscount = this.tierDiscount(tier, subtotal, compareAtTotal);
      const unroundedTotal = subtotal - Math.min(subtotal, tier.quantity === 1 ? 0 : requestedDiscount);
      const total = tier.discountType === 'percentage'
        ? Math.min(subtotal, this.roundUpToNextNineHundred(unroundedTotal))
        : unroundedTotal;
      const discount = subtotal - total;
      const savings = compareAtTotal !== null
        ? Math.max(0, compareAtTotal - total)
        : discount;
      return { subtotal, discount, total, compareAtTotal, savings };
    }

    completedPairCount(tier) {
      return Array.from({ length: tier.quantity }, (_, index) => this.selectedVariant(index)).filter(Boolean).length;
    }

    formatMoney(cents) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0,
      }).format(Math.round(cents / 100));
    }

    pairLabel(quantity) {
      return `${quantity} ${quantity === 1 ? 'par' : 'pares'}`;
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      this.mobileBuyBar?.remove();
      document.body.classList.remove('vanta-offer-mobile-visible');
    }

    colorValue(value) {
      const normalized = String(value).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (normalized.includes('negro')) return '#171717';
      if (normalized.includes('blanco')) return '#f6f6f4';
      if (normalized.includes('azul')) return '#2774b8';
      if (normalized.includes('verde')) return '#6d9d47';
      if (normalized.includes('rojo')) return '#ca3d35';
      if (normalized.includes('amarillo')) return '#dfb62f';
      if (normalized.includes('naranja')) return '#e66b2e';
      if (normalized.includes('rosa') || normalized.includes('fucsia')) return '#d270a5';
      if (normalized.includes('morado') || normalized.includes('violeta')) return '#8061a8';
      if (normalized.includes('gris')) return '#9b9b9b';
      if (normalized.includes('beige') || normalized.includes('crema')) return '#d9c7a9';
      if (normalized.includes('cafe') || normalized.includes('marron')) return '#74513f';
      return '#c7c7c7';
    }

    colorImage(optionIndex, value) {
      const matchingVariants = this.variants.filter(
        (candidate) => candidate.options?.[optionIndex] === value && candidate.image
      );
      const variant = matchingVariants.find((candidate) => candidate.available) || matchingVariants[0];
      return variant?.image || '';
    }

    escape(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('"', '&quot;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
    }

    renderTier(tier) {
      const pricing = this.tierPricing(tier);
      const selected = tier.id === this.selectedTierId;
      const savings = tier.showSavings && pricing.savings > 0
        ? `<span class="vanta-offer__tier-saving">Ahorras ${this.formatMoney(pricing.savings)}</span>`
        : '';
      const compareAtPrice = pricing.compareAtTotal !== null
        ? `<small class="vanta-offer__tier-compare">Antes ${this.formatMoney(pricing.compareAtTotal)}</small>`
        : '';
      const badge = tier.badge ? `<span class="vanta-offer__badge">${this.escape(tier.badge)}</span>` : '';
      const label = tier.label || 'Precio especial';

      const configuration = selected ? this.renderTierConfiguration(tier) : '';

      return `<div class="vanta-offer__tier-wrap${selected ? ' is-selected' : ''}">
        <div class="vanta-offer__tier-surface">
        <button class="vanta-offer__tier${selected ? ' is-selected' : ''}" type="button" data-tier="${this.escape(tier.id)}" aria-pressed="${selected}">
        <span class="vanta-offer__tier-check" aria-hidden="true"></span>
        <span class="vanta-offer__tier-copy">
          <span class="vanta-offer__tier-title-row">
            <strong>${this.pairLabel(tier.quantity)}</strong>
            ${badge}
          </span>
          <small>${this.escape(label)}</small>
          ${savings}
        </span>
        <span class="vanta-offer__tier-price">
          ${compareAtPrice}
          <strong>${this.formatMoney(pricing.total)}</strong>
        </span>
        </button>
        ${configuration}
        </div>
      </div>`;
    }

    renderOption(pairIndex, optionIndex) {
      let selected = this.selections[pairIndex]?.[optionIndex] || '';
      const values = this.optionValues(optionIndex);
      const isColor = this.isColorOption(optionIndex);
      const isSize = this.isSizeOption(optionIndex);
      const validationKey = this.validationKey(pairIndex, optionIndex);
      const invalid = this.validationErrors?.has(validationKey);
      if (pairIndex === 0 && isColor && !selected && !this.firstPairColorInitialized) {
        selected = (this.initialVariant?.available ? this.initialVariant.options?.[optionIndex] : '')
          || this.variants.find((variant) => variant.available)?.options?.[optionIndex]
          || values[0]
          || '';
        this.selections[pairIndex][optionIndex] = selected;
        this.firstPairColorInitialized = true;
      }
      const fieldLabel = isColor || isSize
        ? `${this.escape(this.optionName(optionIndex))}: <span class="vanta-offer__field-value">${this.escape(selected ? this.displayColorValue(selected) : isColor ? 'Selecciona un color' : 'Selecciona una talla')}</span>`
        : this.escape(this.optionName(optionIndex));
      const sizeGuideTrigger = isSize ? this.renderSizeGuideTrigger() : '';

      return `<div class="vanta-offer__field${invalid ? ' is-invalid' : ''}">
        <div class="vanta-offer__field-label${sizeGuideTrigger ? ' vanta-offer__field-label--size' : ''}">
          <span>${fieldLabel}</span>
          ${sizeGuideTrigger}
        </div>
        <div class="vanta-offer__choices${isColor ? ' vanta-offer__choices--color' : ''}" role="radiogroup" aria-label="${this.escape(this.optionName(optionIndex))} para el par ${pairIndex + 1}"${invalid ? ' aria-invalid="true"' : ''}>
          ${values.map((value) => {
            const active = selected === value;
            const available = this.isOptionAvailable(pairIndex, optionIndex, value);
            const image = isColor ? this.colorImage(optionIndex, value) : '';
            const visual = isColor
              ? image
                ? `<img class="vanta-offer__color-image" src="${this.escape(image)}" alt="" loading="lazy">`
                : `<span class="vanta-offer__color-dot" style="--vanta-swatch: ${this.colorValue(value)}"></span>`
              : '';
            const unavailableLabel = available ? '' : ` aria-label="${this.escape(value)}, no disponible" title="No disponible"`;
            return `<button class="vanta-offer__choice${active ? ' is-selected' : ''}${available ? '' : ' is-unavailable'}" type="button" data-pair-option="${pairIndex}" data-option-index="${optionIndex}" data-option-value="${this.escape(value)}" role="radio" aria-checked="${active}"${unavailableLabel}${available ? '' : ' disabled'}>${visual}<span>${this.escape(value)}</span></button>`;
          }).join('')}
        </div>
        ${invalid ? `<span class="vanta-offer__field-error">Selecciona ${this.escape(this.optionName(optionIndex).toLowerCase())}.</span>` : ''}
      </div>`;
    }

    renderSizeGuideTrigger() {
      const drawerId = this.dataset.sizeGuideId;
      if (!drawerId) return '';

      const label = this.dataset.sizeGuideLabel || 'Encuentra tu talla aquí';
      return `<button
        type="button"
        class="vanta-size-guide-trigger vanta-size-guide-trigger--inline"
        aria-haspopup="dialog"
        aria-controls="${this.escape(drawerId)}"
        aria-expanded="false"
        data-vanta-size-guide-open
      >
        <svg class="vanta-size-guide-trigger__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M3.5 7.5h17v9h-17v-9Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"></path>
          <path d="M6.5 7.8v4.2M9.5 7.8v2.8M12.5 7.8v4.2M15.5 7.8v2.8M18.5 7.8v4.2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
        </svg>
        <span>${this.escape(label)}</span>
      </button>`;
    }

    displayColorValue(value) {
      return String(value).toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    renderTierConfiguration(tier) {
      return `<section class="vanta-offer__configuration" aria-live="polite">
        <div class="vanta-offer__configuration-heading">
          <h3>Elige color y talla</h3>
        </div>
        <div class="vanta-offer__pair-editors">${Array.from({ length: tier.quantity }, (_, pairIndex) => this.renderPairEditor(pairIndex, tier)).join('')}</div>
      </section>`;
    }

    renderPairEditor(pairIndex, tier) {
      const pairHeading = tier.quantity > 1
        ? `<div class="vanta-offer__pair-editor-heading"><strong>Par ${pairIndex + 1}</strong></div>`
        : '';

      return `<div class="vanta-offer__pair-editor" data-pair="${pairIndex}">
        ${pairHeading}
        <div class="vanta-offer__options">${Array.from({ length: this.optionCount() }, (_, optionIndex) => this.renderOption(pairIndex, optionIndex)).join('')}</div>
      </div>`;
    }

    render() {
      const tier = this.selectedTier();
      this.resizeSelections(tier.quantity);
      const pricing = this.tierPricing(tier);
      const purchaseCopy = this.purchaseButtonContent(tier, pricing);

      this.innerHTML = `
      <section class="vanta-offer__step vanta-offer__step--offer" aria-label="Elige tu oferta">
        <div class="vanta-offer__tiers">${this.offer.tiers.map((candidate) => this.renderTier(candidate)).join('')}</div>
      </section>
      <div class="vanta-offer__actions">
        <button class="vanta-offer__button vanta-offer__button--buy" type="button">${purchaseCopy}</button>
        <button class="vanta-offer__button vanta-offer__button--cart" type="button">Agregar al carrito</button>
      </div>
      <p class="vanta-offer__error" role="alert" hidden></p>`;

      this.querySelectorAll('[data-tier]').forEach((button) => button.addEventListener('click', () => {
        this.selectedTierId = button.dataset.tier;
        this.validationErrors?.clear();
        this.render();
      }));
      this.querySelectorAll('[data-pair-option]').forEach((button) => button.addEventListener('click', () => {
        this.selectOption(Number(button.dataset.pairOption), Number(button.dataset.optionIndex), button.dataset.optionValue || '');
      }));
      this.querySelector('.vanta-offer__button--cart').addEventListener('click', () => this.addToCart());
      this.querySelector('.vanta-offer__button--buy').addEventListener('click', () => this.buyNow());
      this.renderMobileBuyBar(purchaseCopy);
      requestAnimationFrame(() => this.updateMobileBuyBar());
    }

    purchaseButtonContent(tier, pricing) {
      const icon = document.querySelector('.vanta-buy-now-button__icon')?.innerHTML || '';
      const label = `Comprar ahora · ${this.formatMoney(pricing.total)}`;
      return `<span class="vanta-offer__buy-icon" aria-hidden="true">${icon}</span><span class="vanta-offer__buy-copy"><span class="vanta-offer__buy-main">${label}</span><span class="vanta-offer__buy-methods">Tarjeta · PSE · Contra entrega</span></span>`;
    }

    renderMobileBuyBar(purchaseCopy) {
      if (!this.mobileBuyBar) {
        this.mobileBuyBar = document.createElement('div');
        this.mobileBuyBar.className = 'vanta-offer__mobile-buy';
        this.mobileBuyBar.setAttribute('aria-label', 'Compra rápida');
        this.mobileBuyButton = document.createElement('button');
        this.mobileBuyButton.className = 'vanta-offer__button vanta-offer__button--buy';
        this.mobileBuyButton.type = 'button';
        this.mobileBuyButton.addEventListener('click', () => this.buyNow());
        this.mobileBuyBar.appendChild(this.mobileBuyButton);
        document.body.appendChild(this.mobileBuyBar);
      }

      this.mobileBuyButton.innerHTML = purchaseCopy;
    }

    updateMobileBuyBar() {
      const sourceButton = this.querySelector('.vanta-offer__actions .vanta-offer__button--buy');
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const hasPassedButton = Boolean(isMobile && sourceButton && sourceButton.getBoundingClientRect().bottom <= 0);
      document.body.classList.toggle('vanta-offer-mobile-visible', hasPassedButton);
    }

    selectOption(pairIndex, optionIndex, value) {
      this.selections[pairIndex][optionIndex] = value;
      this.validationErrors?.delete(this.validationKey(pairIndex, optionIndex));
      this.syncProductGallery(optionIndex, value);
      this.render();
    }

    validationKey(pairIndex, optionIndex) {
      return `${pairIndex}:${optionIndex}`;
    }

    validateSelections() {
      const tier = this.selectedTier();
      const validationErrors = new Set();

      for (let pairIndex = 0; pairIndex < tier.quantity; pairIndex += 1) {
        const options = this.selections[pairIndex] || [];
        for (let optionIndex = 0; optionIndex < this.optionCount(); optionIndex += 1) {
          if (!options[optionIndex]) validationErrors.add(this.validationKey(pairIndex, optionIndex));
        }
      }

      if (!validationErrors.size) return true;

      this.validationErrors = validationErrors;
      this.render();
      this.querySelector('.vanta-offer__field.is-invalid')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    syncProductGallery(optionIndex, value) {
      if (!this.isColorOption(optionIndex)) return;

      const variant = this.variants.find(
        (candidate) => candidate.available && candidate.options?.[optionIndex] === value && candidate.mediaId
      );
      this.syncProductGalleryForVariant(variant);
    }

    syncProductGalleryForVariant(variant) {
      const productInfo = this.closest('product-info');
      if (!productInfo || !variant) return;

      const requestId = (this.mediaRequestId || 0) + 1;
      this.mediaRequestId = requestId;

      const requestUrl = new URL(productInfo.dataset.url, window.location.origin);
      requestUrl.searchParams.set('variant', variant.id);
      requestUrl.searchParams.set('section_id', productInfo.sectionId || productInfo.dataset.section);

      fetch(requestUrl)
        .then((response) => {
          if (!response.ok) throw new Error('Unable to load product media');
          return response.text();
        })
        .then((responseText) => {
          if (this.mediaRequestId !== requestId) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          productInfo.updateMedia?.(html, variant.mediaId);
        })
        .catch(() => {});
    }

    buildDraft(source) {
      const groupId = crypto.randomUUID();
      const lines = this.selections.map((options) => {
        const variant = this.findMatchingVariant(options);
        if (!variant || options.some((option) => !option)) {
          throw new Error('Selecciona una combinación disponible para cada par.');
        }
        return {
          variantId: `gid://shopify/ProductVariant/${variant.id}`,
          quantity: 1,
          offerId: this.offer.id,
          offerTierId: this.selectedTierId,
          offerGroupId: groupId,
        };
      });
      return {
        source,
        groups: [{ id: groupId, productId: this.dataset.productId, offerId: this.offer.id, offerTierId: this.selectedTierId, lines }],
      };
    }

    setBusy(busy) {
      this.querySelectorAll('.vanta-offer__button').forEach((button) => { button.disabled = busy; });
      if (this.mobileBuyButton) this.mobileBuyButton.disabled = busy;
    }

    showError(message) {
      const error = this.querySelector('.vanta-offer__error');
      error.textContent = message;
      error.hidden = !message;
    }

    cleanOfferProperties(properties) {
      const cleanProperties = { ...(properties || {}) };
      Object.keys(cleanProperties).forEach((key) => {
        if (key.startsWith('_vanta_offer_')) delete cleanProperties[key];
      });
      return cleanProperties;
    }

    offerProperties(properties, groupId, tierId) {
      return {
        ...this.cleanOfferProperties(properties),
        _vanta_offer_id: this.offer.id,
        _vanta_offer_group: groupId,
        _vanta_offer_tier: tierId,
      };
    }

    async reconcileCartOfferGroups() {
      const cart = await fetch('/cart.js').then((response) => response.json());
      const productId = String(this.dataset.productId).split('/').pop();
      const matchingItems = cart.items.filter((item) => String(item.product_id) === productId && !item.selling_plan_allocation);
      if (!matchingItems.length) return null;

      const units = matchingItems.flatMap((item) => Array.from({ length: item.quantity }, () => ({
        id: item.variant_id,
        properties: item.properties,
      })));
      const tiers = [...this.offer.tiers].sort((left, right) => right.quantity - left.quantity);
      const items = [];

      while (units.length) {
        const tier = tiers.find((candidate) => candidate.quantity <= units.length);
        if (!tier) {
          const unit = units.shift();
          items.push({ id: unit.id, quantity: 1, properties: this.cleanOfferProperties(unit.properties) });
          continue;
        }

        const groupId = crypto.randomUUID();
        units.splice(0, tier.quantity).forEach((unit) => {
          items.push({
            id: unit.id,
            quantity: 1,
            properties: {
              ...this.offerProperties(unit.properties, groupId, tier.id),
              _vanta_offer_quantity: String(tier.quantity),
            },
          });
        });
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: Object.fromEntries(matchingItems.map((item) => [item.key, 0])) }),
      }).then(async (response) => {
        if (!response.ok) throw new Error('No pudimos actualizar la oferta en el carrito.');
      });

      const result = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      }).then((response) => response.json());
      if (result.status) throw new Error(result.description || 'No pudimos actualizar la oferta en el carrito.');
      return result;
    }

    async addToCart() {
      try {
        if (!this.validateSelections()) return;
        this.setBusy(true);
        this.showError('');
        const draft = this.buildDraft('cart');
        const group = draft.groups[0];
        const tier = this.selectedTier();
        let result = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: group.lines.map((line) => ({
              id: line.variantId.split('/').pop(),
              quantity: 1,
              properties: {
                _vanta_offer_id: group.offerId,
                _vanta_offer_group: group.id,
                _vanta_offer_tier: group.offerTierId,
                _vanta_offer_quantity: String(tier.quantity),
              },
            })),
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        }).then((response) => response.json());
        if (result.status) throw new Error(result.description || 'No pudimos agregar la oferta.');

        const groupedResult = await this.reconcileCartOfferGroups();
        if (groupedResult) result = groupedResult;

        const drawer = document.querySelector('cart-drawer');
        if (drawer?.renderContents && result.sections) drawer.renderContents(result);
        else window.location.assign('/cart');
      } catch (error) {
        this.showError(error.message || 'No pudimos agregar la oferta.');
      } finally {
        this.setBusy(false);
      }
    }

    buyNow() {
      try {
        if (!this.validateSelections()) return;
        const draft = this.buildDraft('buy_now');
        sessionStorage.setItem('vanta_purchase_draft', JSON.stringify(draft));
        window.location.assign('/cart?view=vanta-checkout&source=buy_now');
      } catch (error) {
        this.showError(error.message || 'No pudimos preparar la compra.');
      }
    }
  });
}
